/**
 * ShopifyConnectPanel – Shopify integration panel.
 * Primary path: OAuth (klient wpisuje tylko domenę sklepu, klika Połącz, autoryzuje w Shopify).
 * Fallback (advanced): Custom App z ręcznie wklejonym Access Token + Domain.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  ShoppingBag,
  TrendingUp,
  Package,
  ChevronDown,
  ChevronUp,
  ExternalLink,
} from "lucide-react";

interface ShopifyStatus {
  connected: boolean;
  shopDomain?: string;
  shopName?: string;
}
interface ShopifyConnectPanelProps {
  onInsightsLoaded?: (context: string) => void;
  forUserId?: string;
}

const ShopifyLogo = () => (
  <div className="w-6 h-6 rounded bg-[#95BF47] flex items-center justify-center">
    <ShoppingBag className="w-3.5 h-3.5 text-white" />
  </div>
);

export default function ShopifyConnectPanel({
  onInsightsLoaded,
  forUserId,
}: ShopifyConnectPanelProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [shopDomain, setShopDomain] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [insightsData, setInsightsData] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const fuParam = forUserId ? `?forUserId=${forUserId}` : "";

  const { data: status, isLoading } = useQuery<ShopifyStatus>({
    queryKey: ["shopify-integration-status", forUserId],
    queryFn: () => apiRequest("GET", `/api/integrations/shopify/status${fuParam}`),
    staleTime: 30_000,
  });

  // OAuth install: ask backend to build the install URL, then redirect the
  // browser to Shopify so the merchant approves the app and we exchange the
  // code on our /oauth/callback endpoint.
  async function handleOAuthInstall() {
    if (!shopDomain.trim()) return;
    setOauthLoading(true);
    try {
      const installFu = forUserId ? `&forUserId=${forUserId}` : "";
      const res = (await apiRequest(
        "GET",
        `/api/integrations/shopify/oauth/install?shop=${encodeURIComponent(shopDomain.trim())}${installFu}`
      )) as { url: string };
      if (res?.url) {
        window.location.href = res.url;
      } else {
        toast({
          title: "Shopify",
          description: "Nie udało się zbudować linku instalacyjnego.",
          variant: "destructive",
        });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Błąd OAuth";
      toast({ title: "Shopify OAuth", description: msg, variant: "destructive" });
    } finally {
      setOauthLoading(false);
    }
  }

  const connectMutation = useMutation({
    mutationFn: (params: { shopDomain: string; accessToken: string }) =>
      apiRequest("POST", "/api/integrations/shopify/connect", params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shopify-integration-status"] });
      setShopDomain("");
      setAccessToken("");
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/integrations/shopify/disconnect"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shopify-integration-status"] });
      setInsightsData(null);
    },
  });

  const insightsMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/integrations/shopify/insights"),
    onSuccess: (data: any) => {
      setInsightsData(data);
      onInsightsLoaded?.(data.context);
    },
  });

  const [pixelSnippetLoading, setPixelSnippetLoading] = useState(false);
  async function copyPixelSnippet() {
    setPixelSnippetLoading(true);
    try {
      const res = (await apiRequest("GET", "/api/integrations/shopify/custom-pixel-snippet")) as {
        snippet: string;
      };
      await navigator.clipboard.writeText(res.snippet);
      toast({
        title: "Shopify",
        description:
          "Custom Pixel skopiowany. Wklej go w Shopify: Ustawienia → Zdarzenia klientów → Dodaj niestandardowy piksel.",
      });
    } catch (err) {
      toast({
        title: "Shopify",
        description: err instanceof Error ? err.message : "Nie udało się pobrać snippetu.",
        variant: "destructive",
      });
    } finally {
      setPixelSnippetLoading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-green-400" />
        <span className="text-sm text-white/60">{t("connectPanels.shopify.checking")}</span>
      </div>
    );
  }

  if (!status?.connected) {
    return (
      <div className="p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShopifyLogo />
            <span className="text-sm font-medium text-white">Shopify</span>
            <Badge variant="outline" className="text-xs border-white/[0.1] text-white/60">
              {t("connectPanels.common.notConnected")}
            </Badge>
          </div>
        </div>
        <p className="text-xs text-white/50">
          Wpisz domenę swojego sklepu Shopify (np.{" "}
          <code className="text-white/70">moj-sklep.myshopify.com</code>) i kliknij{" "}
          <strong>Połącz</strong>. Zostaniesz przekierowany do Shopify żeby autoryzować integrację —
          wystarczy jeden klik.
        </p>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={shopDomain}
            onChange={(e) => setShopDomain(e.target.value)}
            placeholder="moj-sklep.myshopify.com"
            className="flex-1 h-9 px-3 text-sm bg-[#030407] border border-white/[0.1] rounded-md text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-[#95BF47]/50"
            onKeyDown={(e) => {
              if (e.key === "Enter") handleOAuthInstall();
            }}
          />
          <Button
            size="sm"
            onClick={handleOAuthInstall}
            disabled={oauthLoading || !shopDomain.trim()}
            className="bg-[#95BF47] hover:bg-[#7ea73d] text-white text-xs"
          >
            {oauthLoading ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin mr-1" /> Łączę…
              </>
            ) : (
              <>
                <ExternalLink className="h-3 w-3 mr-1" /> Połącz przez Shopify
              </>
            )}
          </Button>
        </div>

        {/* Advanced fallback: Custom App with manual access token */}
        <div className="pt-2 border-t border-white/[0.06]">
          <button
            onClick={() => setShowAdvanced((v) => !v)}
            className="text-[11px] text-white/40 hover:text-white/60 inline-flex items-center gap-1"
          >
            {showAdvanced ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            Zaawansowane: ręczny Access Token (Custom App)
          </button>
          {showAdvanced && (
            <div className="mt-2 space-y-2">
              <p className="text-[11px] text-white/40">
                Tylko jeśli masz Custom App w Shopify Partners. W innym wypadku zostaw wyżej i
                kliknij „Połącz przez Shopify".
              </p>
              <input
                type="password"
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                placeholder={t("connectPanels.shopify.tokenPlaceholder")}
                className="w-full h-8 px-3 text-xs bg-[#030407] border border-white/[0.1] rounded-md text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-[#95BF47]/50"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => connectMutation.mutate({ shopDomain, accessToken })}
                disabled={connectMutation.isPending || !shopDomain.trim() || !accessToken.trim()}
                className="text-xs"
              >
                {connectMutation.isPending ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />{" "}
                    {t("connectPanels.shopify.checkingButton")}
                  </>
                ) : (
                  "Połącz przez Custom App"
                )}
              </Button>
              {connectMutation.isError && (
                <p className="text-xs text-amber-400/70">{t("connectPanels.shopify.errorHint")}</p>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 bg-white/[0.04] border border-[#95BF47]/30 rounded-lg space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShopifyLogo />
          <span className="text-sm font-medium text-white">Shopify</span>
          <Badge className="text-xs bg-green-600/20 text-green-400 border-green-500/50">
            {t("connectPanels.common.connected")} · {status.shopName || status.shopDomain}
          </Badge>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => disconnectMutation.mutate()}
          disabled={disconnectMutation.isPending}
          className="text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
        >
          {t("connectPanels.common.disconnect")}
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={() => insightsMutation.mutate()}
          disabled={insightsMutation.isPending}
          className="bg-[#95BF47] hover:bg-[#7ea73d] text-white text-xs"
        >
          {insightsMutation.isPending ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin mr-1" /> {t("connectPanels.common.fetching")}
            </>
          ) : (
            t("connectPanels.common.fetch")
          )}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => copyPixelSnippet()}
          disabled={pixelSnippetLoading}
          className="text-xs"
        >
          {pixelSnippetLoading ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin mr-1" /> Pobieram…
            </>
          ) : (
            "Kopiuj Custom Pixel"
          )}
        </Button>
      </div>
      <p className="text-[11px] text-white/40">
        Custom Pixel wklej w Shopify: Ustawienia → Zdarzenia klientów → Dodaj niestandardowy piksel.
        Bez niego konwersje z checkoutu mają niższy Event Match Quality.
      </p>

      {insightsData && insightsData.summary && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <Package className="h-3.5 w-3.5 text-green-400" />
                <span className="text-[11px] text-white/60 uppercase">
                  {t("connectPanels.common.products")}
                </span>
              </div>
              <div className="text-xl font-bold text-green-400 mt-1">
                {insightsData.summary.totalProducts}
              </div>
              <span className="text-[10px] text-white/40">
                {t("connectPanels.shopify.activeProducts", {
                  count: insightsData.summary.activeProducts,
                })}
              </span>
            </div>
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <ShoppingBag className="h-3.5 w-3.5 text-blue-400" />
                <span className="text-[11px] text-white/60 uppercase">
                  {t("connectPanels.common.orders")}
                </span>
              </div>
              <div className="text-xl font-bold text-blue-400 mt-1">
                {insightsData.summary.totalOrders}
              </div>
            </div>
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-3.5 w-3.5 text-emerald-400" />
                <span className="text-[11px] text-white/60 uppercase">
                  {t("connectPanels.common.revenue")}
                </span>
              </div>
              <div className="text-xl font-bold text-emerald-400 mt-1">
                {(Number(insightsData.summary.totalRevenue) || 0).toFixed(0)}{" "}
                {insightsData.summary.currency ?? ""}
              </div>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-3.5 w-3.5 text-amber-400" />
                <span className="text-[11px] text-white/60 uppercase">
                  {t("connectPanels.shopify.avgOrder")}
                </span>
              </div>
              <div className="text-xl font-bold text-amber-400 mt-1">
                {(Number(insightsData.summary.avgOrderValue) || 0).toFixed(0)}{" "}
                {insightsData.summary.currency ?? ""}
              </div>
            </div>
          </div>

          {insightsData.summary.topProducts?.length > 0 && (
            <div>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex items-center gap-1.5 text-xs text-white/60 hover:text-white/70 transition-colors w-full"
              >
                {showDetails ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
                <span className="font-medium">
                  {t("connectPanels.shopify.topProducts", {
                    count: Math.min(insightsData.summary.topProducts.length, 10),
                  })}
                </span>
              </button>
              {showDetails && (
                <div className="mt-2 rounded-lg border border-white/[0.08] overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-[#0a0c10]/80 text-white/60">
                        <th className="text-left p-2 font-medium">
                          {t("connectPanels.common.product")}
                        </th>
                        <th className="text-right p-2 font-medium">
                          {t("connectPanels.common.sold")}
                        </th>
                        <th className="text-right p-2 font-medium">
                          {t("connectPanels.common.revenue")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {insightsData.summary.topProducts.slice(0, 10).map((p: any, i: number) => {
                        const revenue = Number(p.revenue) || 0;
                        const quantity = Number(p.quantity) || 0;
                        return (
                          <tr key={i} className="border-t border-white/[0.06]">
                            <td className="p-2 text-white max-w-[200px] truncate">
                              {p.title ?? "—"}
                            </td>
                            <td className="p-2 text-right text-blue-400">{quantity}</td>
                            <td className="p-2 text-right text-emerald-400">
                              {revenue.toFixed(0)} {insightsData.summary.currency ?? ""}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            <Badge className="text-xs bg-green-600/20 text-green-400 border-green-500/50">
              {t("connectPanels.common.dataLoaded")}
            </Badge>
            <span className="text-xs text-white/40">
              {t("connectPanels.shopify.contextAttached")}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
